<template>
  <div class="date-picker" ref="root">
    <div class="date-picker__trigger" @click="isOpen = !isOpen">
      <input
        :value="displayValue"
        class="date-picker__input"
        :placeholder="$t('datePicker.placeholder')"
        readonly
        @click.stop="isOpen = !isOpen"
      />
    </div>

    <div v-if="isOpen" class="date-picker__dropdown" @click.stop>
      <div class="date-picker__header">
        <button class="date-picker__nav-btn" type="button" @click="prevMonth" :title="$t('datePicker.prevMonth')">&lt;</button>
        <div class="date-picker__month-year">
          <select class="date-picker__select" v-model.number="currentMonth">
            <option v-for="(name, idx) in monthNames" :key="idx" :value="idx">{{ name }}</option>
          </select>
          <select class="date-picker__select" v-model.number="currentYear">
            <option v-for="y in yearList" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <button class="date-picker__nav-btn" type="button" @click="nextMonth" :title="$t('datePicker.nextMonth')">&gt;</button>
      </div>

      <div class="date-picker__weekdays">
        <div v-for="day in weekDayNames" :key="day" class="date-picker__weekday">
          {{ day }}
        </div>
      </div>

      <div class="date-picker__days">
        <button
          v-for="(day, idx) in daysInMonth"
          :key="idx"
          type="button"
          class="date-picker__day"
          :class="{ 'is-selected': isSelectedDay(day), 'is-today': isToday(day) }"
          @click="selectDay(day)"
          :disabled="!day"
        >
          {{ day }}
        </button>
      </div>

      <div class="date-picker__footer">
        <button class="date-picker__btn-clear" type="button" @click="clearDate">{{ $t('datePicker.clear') }}</button>
        <button class="date-picker__btn-today" type="button" @click="setToday">{{ $t('datePicker.today') }}</button>
        <button class="date-picker__btn-confirm" type="button" @click="confirmDate">OK</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DatePicker',
  props: {
    modelValue: { type: String, default: '' },
    // 'display' (DD.MM.YYYY) — формат v-model по умолчанию
    // 'iso' (YYYY-MM-DD) — для совместимости со старыми полями
    format: { type: String, default: 'display' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      selectedDay: null,
      selectedMonth: null,
      selectedYear: null,
    };
  },
  computed: {
    monthNames() {
      return this.$tm('datePicker.months')
    },
    weekDayNames() {
      return this.$tm('datePicker.weekDays')
    },
    displayValue() {
      const v = this.modelValue || '';
      if (this.format === 'iso' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [y, m, d] = v.split('-');
        return `${d}.${m}.${y}`;
      }
      return v;
    },
    daysInMonth() {
      const date = new Date(this.currentYear, this.currentMonth + 1, 0);
      const days = [];
      const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
      const lastDay = date.getDate();

      const startDay = firstDay === 0 ? 6 : firstDay - 1;

      for (let i = 0; i < startDay; i++) {
        days.push(null);
      }

      for (let i = 1; i <= lastDay; i++) {
        days.push(i);
      }

      return days;
    },
    yearList() {
      const now = new Date().getFullYear();
      const start = now - 80;
      const end = now + 10;
      const arr = [];
      for (let y = start; y <= end; y++) arr.push(y);
      return arr;
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocClick);
  },
  methods: {
    onDocClick(e) {
      if (!this.isOpen) return;
      const root = this.$refs.root;
      if (root && !root.contains(e.target)) {
        this.isOpen = false;
      }
    },
    setToday() {
      const t = new Date();
      this.selectedDay = t.getDate();
      this.selectedMonth = t.getMonth();
      this.selectedYear = t.getFullYear();
      this.currentMonth = t.getMonth();
      this.currentYear = t.getFullYear();
      this.confirmDate();
    },
    parseDate(dateStr) {
      if (!dateStr || typeof dateStr !== 'string') return null;
      let m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return { year: +m[1], month: +m[2] - 1, day: +m[3] };
      m = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (m) return { day: +m[1], month: +m[2] - 1, year: +m[3] };
      return null;
    },
    formatDate(day, month, year) {
      const pad = (n) => String(n).padStart(2, '0');
      if (this.format === 'iso') {
        return `${year}-${pad(month + 1)}-${pad(day)}`;
      }
      return `${pad(day)}.${pad(month + 1)}.${year}`;
    },
    isSelectedDay(day) {
      if (!day) return false;
      const parsed = this.parseDate(this.modelValue);
      if (!parsed) return false;
      return day === parsed.day && 
             this.currentMonth === parsed.month && 
             this.currentYear === parsed.year;
    },
    isToday(day) {
      if (!day) return false;
      const today = new Date();
      return day === today.getDate() && 
             this.currentMonth === today.getMonth() && 
             this.currentYear === today.getFullYear();
    },
    selectDay(day) {
      if (!day) return;
      this.selectedDay = day;
      this.selectedMonth = this.currentMonth;
      this.selectedYear = this.currentYear;
    },
    confirmDate() {
      if (this.selectedDay !== null) {
        const formatted = this.formatDate(this.selectedDay, this.selectedMonth, this.selectedYear);
        this.$emit('update:modelValue', formatted);
      }
      this.isOpen = false;
    },
    clearDate() {
      this.$emit('update:modelValue', '');
      this.isOpen = false;
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && typeof newVal === 'string') {
          const parsed = this.parseDate(newVal);
          if (parsed) {
            this.selectedDay = parsed.day;
            this.selectedMonth = parsed.month;
            this.selectedYear = parsed.year;
            this.currentMonth = parsed.month;
            this.currentYear = parsed.year;
          }
        }
      },
    },
  },
};
</script>

<style scoped>
.date-picker {
  position: relative;
  width: 100%;
}

.date-picker__trigger {
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  width: 100%;
}

.date-picker__input {
  width: 100%;
  height: 32px;
  padding: 6px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 16px;
  line-height: 22.2px;
  font-family: 'Times New Roman', Times, serif;
  color: #111827;
  background: #fff;
  cursor: pointer;
}

.date-picker__input:focus {
  outline: 2px solid rgba(47, 120, 238, 0.25);
  outline-offset: 0;
}

.date-picker__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 12px;
  min-width: 280px;
}

.date-picker__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 8px;
}

.date-picker__nav-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #E5E7EB;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  color: #111827;
}

.date-picker__nav-btn:hover {
  background: #F9FAFB;
}

.date-picker__month-year {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}
.date-picker__select {
  border: 1px solid #E5E7EB;
  background: #fff;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
}

.date-picker__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.date-picker__weekday {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #687588;
  padding: 4px;
}

.date-picker__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 12px;
}

.date-picker__day {
  width: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: 1px solid #E5E7EB;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
}

.date-picker__day:hover:not(:disabled) {
  background: #F3F4F6;
}

.date-picker__day:disabled {
  cursor: default;
  color: transparent;
}

.date-picker__day.is-selected {
  background: #2F78EE;
  color: white;
  border-color: #2F78EE;
}

.date-picker__day.is-today {
  border-color: #2F78EE;
  font-weight: 600;
}

.date-picker__footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.date-picker__btn-clear,
.date-picker__btn-today,
.date-picker__btn-confirm {
  padding: 6px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: white;
  color: #111827;
}

.date-picker__btn-today {
  background: #F3F4F6;
}
.date-picker__btn-today:hover {
  background: #E5E7EB;
}

.date-picker__btn-confirm {
  background: #2F78EE;
  color: white;
  border-color: #2F78EE;
}

.date-picker__btn-confirm:hover {
  background: #1e5bb8;
}

.date-picker__btn-clear:hover {
  background: #F9FAFB;
}
</style>